import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient";

// ————— Async Thunks —————
export const fetchICTData = createAsyncThunk("ui/fetchICTData", async (_, { rejectWithValue }) => {
  try {
    const { data } = await apiClient.get("/ictData/");
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const deleteICTData = createAsyncThunk("ui/deleteICTData", async (id, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/ictData/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const updateICTData = createAsyncThunk("ui/updateICTData", async (updatedData, { rejectWithValue }) => {
  try {
    const { id, ...payload } = updatedData;
    const { data } = await apiClient.put(`/ictData/${id}`, payload);
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const uploadICTImage = createAsyncThunk("ui/uploadICTImage", async (file, { rejectWithValue }) => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const { data } = await apiClient.post("/upload-image/", formData, {
      headers: { "Content-Type": "multipart/form-data" }, // override default JSON
    });
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const insertICTData = createAsyncThunk("ui/insertICTData", async (payload, { rejectWithValue }) => {
  try {
    const { data } = await apiClient.post("/ictData/", payload);
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const patchIctDataStatus = createAsyncThunk(
  "ui/patchIctDataStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await apiClient.patch(`/ictData/${id}/status`, { status });
      return res.data; 
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ————— State & Reducers —————
const initialState = {
  ictData: [],
  studyModes: [],
  loading: false,
  error: null,
  uploadPath: null,
  insertSuccess: false,
  isSidebarCollapsed: false,
  activeItem: "Home",
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.isSidebarCollapsed = !state.isSidebarCollapsed;
    },
    setActiveItem: (state, action) => {
      state.activeItem = action.payload;
    },
    resetInsertSuccess: (state) => {
      state.insertSuccess = false;
    },
    clearUploadPath: (state) => {
      state.uploadPath = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchICTData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchICTData.fulfilled, (state, action) => {
        state.loading = false;
        state.ictData = action.payload;
      })
      .addCase(fetchICTData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteICTData.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteICTData.fulfilled, (state, action) => {
        state.loading = false;
        state.ictData = state.ictData.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteICTData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateICTData.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateICTData.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index = state.ictData.findIndex((item) => item.id === updated.id);
        if (index !== -1) state.ictData[index] = updated;
      })
      .addCase(updateICTData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(uploadICTImage.pending, (state) => {
        state.loading = true;
        state.uploadPath = null;
      })
      .addCase(uploadICTImage.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadPath = action.payload.filename;
      })
      .addCase(uploadICTImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(insertICTData.pending, (state) => {
        state.loading = true;
        state.insertSuccess = false;
      })
      .addCase(insertICTData.fulfilled, (state) => {
        state.loading = false;
        state.insertSuccess = true;
      })
      .addCase(insertICTData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(patchIctDataStatus.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.ictData.findIndex((i) => i.id === updated.id);
        if (idx !== -1) state.ictData[idx] = updated;
      })
      .addCase(patchIctDataStatus.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  toggleSidebar,
  setActiveItem,
  resetInsertSuccess,
  clearUploadPath,
} = uiSlice.actions;

export default uiSlice.reducer;
